import { useLocation } from "wouter";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { TrialExhaustedPayload } from "@/lib/trial-exhausted";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: TrialExhaustedPayload | null;
};

export function TrialExhaustedDialog({ open, onOpenChange, payload }: Props) {
  const [, setLocation] = useLocation();
  const limit = payload?.limit;
  const used = payload?.used;
  const apiMessage = payload?.message?.trim();
  // Paid plans have monthly allowances too — swap the Starter-specific copy.
  const paidPlanName =
    payload?.plan === "plus" ? "Plus" : payload?.plan === "premium" ? "Premium" : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="z-[100]"
        className="z-[101] sm:max-w-[420px] rounded-3xl border border-white/70 bg-background/95 p-0 gap-0 shadow-2xl backdrop-blur-md overflow-hidden"
      >
        <div className="relative px-6 pt-10 pb-2">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/[0.07] via-transparent to-lavender/20" />
          <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/12 text-primary ring-1 ring-primary/15 shadow-inner">
            <Sparkles className="h-8 w-8" strokeWidth={1.5} />
          </div>
          <DialogHeader className="relative space-y-3">
            <DialogTitle className="font-display text-center text-2xl font-medium tracking-tight text-foreground">
              {paidPlanName
                ? "You've used this month's unfoldings"
                : "You've reached your Starter limit"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-center text-[15px] leading-relaxed text-muted-foreground">
                {paidPlanName ? (
                  <p>
                    {paidPlanName} includes {limit ?? "a set number of"} unfoldings each month, and
                    you&apos;ve made the most of them. Your allowance refreshes on the 1st.
                  </p>
                ) : (
                  <>
                    <p>
                      Starter includes a handful of Unfoldings so you can feel what Unfold is like.
                      You&apos;ve used those — and we&apos;d love to keep walking with you.
                    </p>
                    {limit != null && (
                      <p className="text-sm text-muted-foreground/90">
                        {used != null && used > limit
                          ? `Starter includes ${limit} complimentary ${limit === 1 ? "Unfolding" : "Unfoldings"}.`
                          : `That was ${limit} complimentary ${limit === 1 ? "Unfolding" : "Unfoldings"} on the house.`}
                      </p>
                    )}
                  </>
                )}
                {apiMessage && (
                  <p className="rounded-2xl border border-black/[0.06] bg-white/50 px-4 py-3 text-sm text-foreground/75">
                    {apiMessage}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {paidPlanName === "Plus"
                    ? "Need more room? Premium includes 120 unfoldings a month."
                    : paidPlanName === "Premium"
                      ? "See you on the 1st — your journal isn't going anywhere."
                      : "Upgrade to Plus or Premium when you're ready — same calm space, no rush."}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
        </div>
        <DialogFooter className="relative flex-col gap-2 border-t border-black/[0.05] bg-muted/20 px-6 py-5 sm:flex-col">
          <Button
            className="w-full rounded-2xl h-12 text-base font-medium shadow-sm"
            onClick={() => {
              onOpenChange(false);
              setLocation("/pricing");
            }}
          >
            See plans &amp; upgrade
          </Button>
          <Button variant="ghost" className="w-full rounded-2xl h-11 text-muted-foreground" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
